// Global Modal Utility for AOX
// Replaces default browser alerts with styled modals

type ModalType = 'success' | 'error' | 'warning' | 'info';

interface ModalOptions {
  type?: ModalType;
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  showCancel?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
}

interface ToastOptions {
  type?: ModalType;
  title?: string;
  message: string;
  duration?: number;
}

// SVG Icons
const icons = {
  success: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>`,
  error: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="15" y1="9" x2="9" y2="15"></line>
    <line x1="9" y1="9" x2="15" y2="15"></line>
  </svg>`,
  warning: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
    <line x1="12" y1="9" x2="12" y2="13"></line>
    <line x1="12" y1="17" x2="12.01" y2="17"></line>
  </svg>`,
  info: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <circle cx="12" cy="12" r="10"></circle>
    <line x1="12" y1="16" x2="12" y2="12"></line>
    <line x1="12" y1="8" x2="12.01" y2="8"></line>
  </svg>`,
  close: `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>`
};

// Default titles for each type
const defaultTitles: Record<ModalType, string> = {
  success: 'Success',
  error: 'Error',
  warning: 'Warning',
  info: 'Notice'
};

// Ensure toast container exists
function ensureToastContainer(): HTMLElement {
  let container = document.getElementById('aox-toast-container');
  if (!container) {
    container = document.createElement('div');
    container.id = 'aox-toast-container';
    container.className = 'aox-toast-container';
    document.body.appendChild(container);
  }
  return container;
}

// Show Modal
export function showModal(options: ModalOptions): Promise<boolean> {
  return new Promise((resolve) => {
    const type = options.type || 'info';
    const title = options.title || defaultTitles[type];
    const confirmText = options.confirmText || 'OK';
    const cancelText = options.cancelText || 'Cancel';
    const showCancel = options.showCancel ?? false;

    // Create overlay
    const overlay = document.createElement('div');
    overlay.className = 'aox-modal-overlay';
    overlay.innerHTML = `
      <div class="aox-modal">
        <div class="aox-modal-header">
          <div class="aox-modal-icon ${type}">
            ${icons[type]}
          </div>
          <h3 class="aox-modal-title">${title}</h3>
        </div>
        <div class="aox-modal-body">
          <p class="aox-modal-message">${options.message}</p>
        </div>
        <div class="aox-modal-footer">
          ${showCancel ? `<button class="aox-modal-btn aox-modal-btn-secondary" data-action="cancel">${cancelText}</button>` : ''}
          <button class="aox-modal-btn aox-modal-btn-primary" data-action="confirm">${confirmText}</button>
        </div>
      </div>
    `;

    document.body.appendChild(overlay);

    // Trigger animation
    requestAnimationFrame(() => {
      overlay.classList.add('show');
    });

    // Close function
    const close = (confirmed: boolean) => {
      overlay.classList.remove('show');
      setTimeout(() => {
        overlay.remove();
        if (confirmed) {
          options.onConfirm?.();
        } else {
          options.onCancel?.();
        }
        resolve(confirmed);
      }, 200);
    };

    // Event listeners
    overlay.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target === overlay) {
        close(false);
      }
      if (target.dataset.action === 'confirm') {
        close(true);
      }
      if (target.dataset.action === 'cancel') {
        close(false);
      }
    });

    // Escape key
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        close(false);
        document.removeEventListener('keydown', handleEscape);
      }
    };
    document.addEventListener('keydown', handleEscape);
  });
}

// Show Toast
export function showToast(options: ToastOptions): void {
  const container = ensureToastContainer();
  const type = options.type || 'info';
  const title = options.title || defaultTitles[type];
  const duration = options.duration ?? 4000;

  const toast = document.createElement('div');
  toast.className = 'aox-toast';
  toast.innerHTML = `
    <div class="aox-toast-icon ${type}">
      ${icons[type]}
    </div>
    <div class="aox-toast-content">
      <p class="aox-toast-title">${title}</p>
      <p class="aox-toast-message">${options.message}</p>
    </div>
    <button class="aox-toast-close">
      ${icons.close}
    </button>
    ${duration > 0 ? '<div class="aox-toast-progress"></div>' : ''}
  `;

  container.appendChild(toast);

  // Trigger animation
  requestAnimationFrame(() => {
    toast.classList.add('show');
  });

  // Close function
  const close = () => {
    toast.classList.remove('show');
    setTimeout(() => {
      toast.remove();
    }, 300);
  };

  // Close button
  toast.querySelector('.aox-toast-close')?.addEventListener('click', close);

  // Auto close
  if (duration > 0) {
    const progressBar = toast.querySelector('.aox-toast-progress') as HTMLElement;
    if (progressBar) {
      progressBar.style.animationDuration = `${duration}ms`;
    }
    setTimeout(close, duration);
  }
}

// Convenience functions
export function showSuccess(message: string, title?: string): void {
  showToast({ type: 'success', message, title });
}

export function showError(message: string, title?: string): void {
  showToast({ type: 'error', message, title, duration: 5000 });
}

export function showWarning(message: string, title?: string): void {
  showToast({ type: 'warning', message, title });
}

export function showInfo(message: string, title?: string): void {
  showToast({ type: 'info', message, title });
}

// Confirm dialog (replacement for window.confirm)
export function showConfirm(message: string, options?: Partial<ModalOptions>): Promise<boolean> {
  return showModal({
    type: 'warning',
    message,
    showCancel: true,
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    ...options
  });
}

// Alert replacement (shows modal)
export function showAlert(message: string, type: ModalType = 'info'): Promise<boolean> {
  return showModal({
    type,
    message
  });
}

// Image Lightbox
export function showLightbox(imageUrl: string): void {
  // Create overlay
  const overlay = document.createElement('div');
  overlay.className = 'aox-lightbox-overlay';
  overlay.innerHTML = `
    <button class="aox-lightbox-close">
      ${icons.close}
    </button>
    <img src="${imageUrl}" alt="Full size image" class="aox-lightbox-image" />
  `;

  document.body.appendChild(overlay);

  // Prevent body scroll
  document.body.style.overflow = 'hidden';

  // Trigger animation
  requestAnimationFrame(() => {
    overlay.classList.add('show');
  });

  // Close function
  const close = () => {
    overlay.classList.remove('show');
    document.body.style.overflow = '';
    setTimeout(() => {
      overlay.remove();
    }, 250);
  };

  // Click anywhere to close (except on image for better UX)
  overlay.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (target === overlay || target.closest('.aox-lightbox-close')) {
      close();
    }
  });

  // Escape key to close
  const handleEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      close();
      document.removeEventListener('keydown', handleEscape);
    }
  };
  document.addEventListener('keydown', handleEscape);
}

// Make available globally
declare global {
  interface Window {
    AOXModal: {
      show: typeof showModal;
      toast: typeof showToast;
      success: typeof showSuccess;
      error: typeof showError;
      warning: typeof showWarning;
      info: typeof showInfo;
      confirm: typeof showConfirm;
      alert: typeof showAlert;
      lightbox: typeof showLightbox;
    };
  }
}

// Export to window for non-module scripts
if (typeof window !== 'undefined') {
  window.AOXModal = {
    show: showModal,
    toast: showToast,
    success: showSuccess,
    error: showError,
    warning: showWarning,
    info: showInfo,
    confirm: showConfirm,
    alert: showAlert,
    lightbox: showLightbox
  };
}
